import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { connect, useDispatch } from 'react-redux'
import { SAVE_PRISMIC_DOCUMENTS } from '../redux/actions/prismic'
import {
  makeStyles,
  createStyles,
  Typography,
  Container,
  Grid,
  useTheme,
} from '@material-ui/core'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import Link from '../components/Link/Link'
import { KeyboardArrowRight } from '@material-ui/icons'
import Skeleton from '@material-ui/lab/Skeleton'
import { useTranslation } from 'react-i18next'
import { InferGetStaticPropsType } from 'next/types'

import Button from '../components/Button/Button'
import Layout from '../components/Layout/Layout'
import InstaWall from '../components/InstaWall/InstaWall'
import { getTranslations } from '../i18n'
import { setFullLocale } from '../utils/utils'
import RecapOrder from '../components/Order/RecapOrder'
import { InlineWidget } from 'react-calendly'
import { useCalendly } from '../utils/useCalendly'
import { DEBUG, isSubscription } from '../utils/constants'
import { deleteGhostUser, getConfirmationOrder } from '../redux/actions/user'
import { useCart } from '../hooks/useCart'
import CMSText, {
  isPrismicText,
  prismicText,
} from '../components/Global/CMSText'
import { createClient, getSingleDocument, getByType } from '../prismicio'
import { RootState } from '../redux/reducers/rootReducer'
import { GetMapDispatchTypes } from '../interfaces/common/redux'
import { PrismicGeneralConfig } from '../interfaces/common/prismicGeneralConfig'
import { PrismicConfirmation } from '../interfaces/common/prismicConfirmation'
import {
  sendPurchaseGA,
  sendPurchaseGTM,
} from '../utils/sendGAAndGTMPurchaseEvent'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      backgroundColor: theme.extraColors.greyLight,
      padding: theme.spacing(4, 0),
      [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(9, 0),
      },
    },
    rootCalendar: {
      [theme.breakpoints.up('lg')]: {
        paddingTop: 0,
      },
    },
    bannerDisplayedOnAllPages: {
      [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing(12),
      },
    },
    textBloc: {
      padding: theme.spacing(3),
      margin: theme.spacing(0, 0, 2),
      boxShadow: theme.shadows[1],
      background: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
    },
    fullHeightCol: {
      display: 'flex',
    },
    fullHeight: {
      width: '100%',
      alignSelf: 'stretch',
    },
    calendarBloc: {},
    calendarSpacer: {
      [theme.breakpoints.up('lg')]: {
        marginTop: theme.spacing(8.5),
      },
    },
    instaBloc: {
      padding: theme.spacing(1.5),
    },
    title: {},
    description: {},
    contactWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    contactText: {
      marginBottom: theme.spacing(2),
    },
    textWrapper: {
      display: 'flex',
      alignItems: 'center',
      textAlign: 'left',
      '&>div': {
        marginLeft: theme.spacing(2),
      },
    },
    textCenter: {
      textAlign: 'center',
    },
    row: {
      margin: theme.spacing(0, 0, 2),
    },
    container: {
      maxWidth: 1268,
    },
    columnRight: {
      [theme.breakpoints.up('lg')]: {
        '&>div': {
          //minHeight: 670,
        },
      },
    },
    emailButton: {
      textAlign: 'left',
      textTransform: 'none',
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
      },
    },
    emailButtonIcon: {
      width: 'auto',
      maxHeight: '1.5em',
    },
    rowCode: {
      display: 'inline-flex',
      justifyContent: 'flex-start',
    },
    codeImage: {
      paddingRight: theme.spacing(1),
      width: '80px',
    },
    productCard: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      borderRadius: 10,
      padding: theme.spacing(2),
      boxShadow: theme.shadows[1],
    },
    productCardMedia: {
      width: 140,
      height: 108,
      objectFit: 'contain',
    },
    productCardContent: {
      width: 'calc(100% - 150px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: 108,
    },
    imageConfirmation: {
      width: '100%',
    },
    imageWrapper: {
      width: '20%',
    },
    imageMail: {
      width: '100%',
    },
    wysiwyg: {
      '&>*': {
        margin: theme.spacing(0, 0, 2),
        '&:last-child': {
          marginBottom: 0,
        },
      },
      '& img': {
        maxWidth: '100%',
        height: 'auto',
        display: 'inline-block',
      },
    },
    linkCard: {
      display: 'block',
    },
    linkCardImg: {
      maxWidth: '100%',
      height: 'auto',
      display: 'inline-block',
    },
    linkCardTitle: {},
  })
)

function truncateString(str, num) {
  if (str.length <= num) {
    return str
  }
  return str.slice(0, num) + '...'
}

const mapStateToProps = (state: RootState) => ({
  checkout: state.checkout,
  parameters: state.parameters,
  stores: state.stores,
  user: state.user,
})

const mapDispatchToProps = {
  getConfirmationOrder: getConfirmationOrder,
  deleteGhostUser: deleteGhostUser,
}

type TConfirmationProps = InferGetStaticPropsType<typeof getStaticProps> &
  GetMapDispatchTypes<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> & {
    emptyCart: () => any
  }

const Confirmation = ({
  doc,
  main,
  accountRes,
  faqRes,
  instagram,
  products,
  inStoreText,
  checkout,
  parameters,
  stores,
  user,
  takeback,
  getConfirmationOrder,
  deleteGhostUser,
  basket,
  banner,
}: TConfirmationProps) => {
  const { emptyCart } = useCart()
  const classes = useStyles()
  const themes = useTheme()
  const { t } = useTranslation()
  const { trackPageView, trackEvent } = useMatomo()
  const dispatch = useDispatch()
  const mail = prismicText(doc?.data?.email)
  const links = doc?.data?.links
  const router = useRouter()
  const lastOrder = user?.confirmationOrder ?? null
  const noCalendly = process.env.NO_CALENDLY

  const calendly = useCalendly(
    stores?.selectedStore,
    parameters?.items?.in_store
  )
  const [displayPage, setDisplayPage] = useState(false)
  // Token de la commande qui vient d'être passée, capturé au montage avant que
  // emptyCart() ne vide 'CartToken' du localStorage.
  const [confirmationToken, setConfirmationToken] = useState<string | null>(
    null
  )

  useEffect(() => {
    dispatch(
      SAVE_PRISMIC_DOCUMENTS({
        general: main?.data,
        instore: inStoreText?.data,
      })
    )
  }, [main, inStoreText])

  useEffect(() => {
    trackPageView({ documentTitle: 'Confirmation' })
  }, [])

  useEffect(() => {
    // Récupère la commande par token + email (route unifiée invité / connecté)
    // GET /shop-api/order/{token}/email/{email}.
    const email = user?.user?.email ?? user?.ghostUser?.email
    if (confirmationToken && email) {
      getConfirmationOrder(confirmationToken, email)
    }
  }, [confirmationToken, user?.user?.email, user?.ghostUser?.email])

  useEffect(() => {
    if (user?.ghostUser) {
      const handleDeleteGhostUser = (url) => {
        if (url !== '/confirmation') deleteGhostUser()
      }
      router.events.on('routeChangeStart', handleDeleteGhostUser)
      return () => {
        router.events.off('routeChangeStart', handleDeleteGhostUser)
      }
    }
  }, [user?.ghostUser])

  useEffect(() => {
    sendPurchaseGA(lastOrder)
    sendPurchaseGTM(lastOrder)
  }, [lastOrder])

  useEffect(() => {
    if (router.asPath !== DEBUG) {
      if (
        checkout &&
        typeof checkout?.pdfResponse !== 'undefined' &&
        checkout?.pdfResponse !== 'Order completed'
      ) {
        if (localStorage.getItem('Payment type') !== '3dSecure') {
          setDisplayPage(false)
          router.push('/')
        } else {
          setDisplayPage(true)
          localStorage.removeItem('Payment type')
        }
      } else {
        setDisplayPage(true)
      }
    } else {
      setDisplayPage(true)
    }
  }, [checkout, router])

  useEffect(() => {
    // On capture le token AVANT emptyCart() : celui-ci supprime 'CartToken' du
    // localStorage, or on en a besoin pour récupérer la commande par token + email.
    setConfirmationToken(localStorage.getItem('CartToken'))
    emptyCart()
  }, [])

  if (!displayPage) return null

  return (
    <Layout
      mainLogo={main?.data?.logo}
      doc={main}
      t={t}
      inStoreText={inStoreText}
      data-testid="confirmation"
      metaTitle={prismicText(
        doc?.data?.meta_title_confirmation,
        t('texts:general:add_a_title')
      )}
      metaDescription={doc?.data?.meta_description_confirmation}
      metaKeywords={doc?.data?.meta_keywords_confirmation}
      hideTop
      takeback={takeback}
      basket={basket}
      account={accountRes}
      faq={faqRes}
      banner={banner}
    >
      <main
        className={clsx(
          'confirmation',
          'checkout',
          classes.root,
          parameters?.items?.in_store &&
            !noCalendly &&
            calendly.ready &&
            classes.rootCalendar
        )}
      >
        <Container className={classes.container} data-testid={'confirmation'}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <div
                className={clsx(
                  classes.textBloc,
                  parameters?.items?.in_store &&
                    !noCalendly &&
                    calendly.ready &&
                    classes.calendarSpacer
                )}
              >
                <div className={classes.textWrapper}>
                  {doc?.data?.confirmation?.[0]?.confirmationimage.url && (
                    <div className={classes.imageWrapper}>
                      <img
                        className={classes.imageMail}
                        src={
                          doc?.data?.confirmation?.[0]?.confirmationimage.url
                        }
                        alt=""
                      />
                    </div>
                  )}
                  <div>
                    <Typography
                      className={clsx(classes.title, 'confirmation__title')}
                      variant="h3"
                      color="primary"
                    >
                      {prismicText(
                        doc?.data?.confirmation?.[0]?.title,
                        t('texts:general:add_a_title')
                      )}
                    </Typography>
                    <Typography className={classes.description} variant="body1">
                      {prismicText(
                        doc?.data?.confirmation?.[0]?.desc,
                        t('texts:general:add_a_description')
                      )}
                    </Typography>
                  </div>
                </div>
              </div>
              <div className={classes.textBloc}>
                <div className={classes.textCenter}>
                  <Typography
                    className={classes.contactText}
                    variant="body1"
                    component="div"
                  >
                    {
                      <CMSText
                        data={doc?.data?.text}
                        defaultText={t('texts:confirmation:text')}
                      />
                    }
                  </Typography>
                  {isPrismicText(doc?.data?.email) &&
                    (doc?.data?.maillogo?.url ? (
                      <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        component="a"
                        onClick={() =>
                          trackEvent({
                            category: 'Confirmation',
                            href: `/${mail}`,
                            action: `User click to send mail to ${mail}`,
                          })
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`mailto:${mail}`}
                        className={classes.emailButton}
                        startIcon={
                          <img
                            src={doc?.data?.maillogo.url}
                            className={classes.emailButtonIcon}
                          />
                        }
                      >
                        {mail}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        component="a"
                        onClick={() =>
                          trackEvent({
                            category: 'Confirmation',
                            href: `/${mail}`,
                            action: `User click to send mail to ${mail}`,
                          })
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`mailto:${mail}`}
                        className={classes.emailButton}
                      >
                        {mail}
                      </Button>
                    ))}
                </div>
              </div>
              {!!instagram?.data && (
                <div className={clsx(classes.textBloc, classes.instaBloc)}>
                  <InstaWall t={t} isSmall data={instagram?.data} />
                </div>
              )}
            </Grid>

            <Grid
              className={clsx(
                classes.columnRight,
                !parameters?.items?.in_store && classes.fullHeightCol
              )}
              item
              xs={12}
              lg={7}
            >
              {parameters?.items?.in_store && calendly.ready && !noCalendly && (
                <div className={classes.calendarBloc}>
                  <div className={classes.row}>
                    <InlineWidget
                      url={calendly.pickupUrl}
                      styles={{
                        height: '1100px',
                      }}
                      pageSettings={{
                        hideEventTypeDetails: false,
                        hideLandingPageDetails: true,
                        hideGdprBanner: true,
                        primaryColor: themes.palette.primary.main.replace(
                          '#',
                          ''
                        ),
                        textColor: themes.palette.common.black.replace('#', ''),
                      }}
                      prefill={{
                        email: user?.user?.email
                          ? user?.user?.email
                          : user?.ghostUser?.email,
                        firstName: user?.user?.firstName
                          ? user?.user?.firstName
                          : user?.ghostUser?.firstName,
                        lastName: user?.user?.lastName
                          ? user?.user?.lastName
                          : user?.ghostUser?.lastName,
                        customAnswers: {
                          a1:
                            lastOrder?.items?.length > 0
                              ? lastOrder?.number
                              : '',
                          a2: user?.user?.phoneNumber
                            ? user?.user?.phoneNumber
                            : '',
                        },
                      }}
                    />
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  classes.textBloc,
                  !parameters?.items?.in_store && classes.fullHeight
                )}
              >
                {lastOrder && (
                  <RecapOrder
                    doc={doc}
                    settings={main?.data}
                    t={t}
                    showSizes={main?.data?.show_size_name}
                    showPrice={!main?.data?.only_add_with_pack}
                    lastOrder={lastOrder}
                    takeBack={takeback}
                  />
                )}
                {(!isSubscription || doc?.data?.active_on_sub) && (
                  <>
                    <div className={classes.row}>
                      <Typography variant="h4" component="h3">
                        {' '}
                        {prismicText(
                          doc?.data?.title_complements,
                          t('confirmation:title_complements')
                        )}
                      </Typography>
                      <Typography variant="body1">
                        {prismicText(
                          doc?.data?.desc_complements,
                          t('confirmation:desc_complements')
                        )}
                      </Typography>
                    </div>
                    <div className={classes.row}>
                      {doc?.data?.codeimage?.url && doc?.data?.codetext && (
                        <div
                          className={clsx(classes.textBloc, classes.rowCode)}
                        >
                          <img
                            src={
                              doc?.data?.codeimage?.url
                                ? doc?.data?.codeimage?.url
                                : '/images/placeholder.jpg'
                            }
                            alt={doc?.data?.codeimage?.url}
                            className={classes.codeImage}
                          />
                          <div>
                            <Typography variant={'body1'}>
                              {prismicText(
                                doc?.data?.codetext,
                                t('confirmation:codetext')
                              )}
                            </Typography>
                            <Typography variant={'body1'} color={'secondary'}>
                              {prismicText(
                                doc?.data?.codename,
                                t('confirmation:codename')
                              )}
                            </Typography>
                          </div>
                        </div>
                      )}
                    </div>
                    {isPrismicText(doc?.data?.succes_text) && (
                      <div className={classes.wysiwyg}>
                        <CMSText data={doc?.data?.succes_text} />
                      </div>
                    )}
                    {links &&
                      links.map(
                        (link, index) =>
                          link?.link_url?.url &&
                          link?.link_url?.url.length > 0 && (
                            <Link href={link.link_url.url} key={index}>
                              <a
                                className={clsx('link-card', classes.linkCard)}
                                target={
                                  link.link_url?.target === '_blank'
                                    ? '_blank'
                                    : '_self'
                                }
                              >
                                <Typography
                                  variant={'h4'}
                                  component="div"
                                  className={clsx(
                                    'link-card__title',
                                    classes.linkCardTitle
                                  )}
                                >
                                  <CMSText data={link?.link_text} />
                                </Typography>
                                {link?.image?.url && (
                                  <img
                                    className={clsx(
                                      'link-card__img',
                                      classes.linkCardImg
                                    )}
                                    src={link?.image?.url}
                                    alt={''}
                                  />
                                )}
                              </a>
                            </Link>
                          )
                      )}
                    {products && (
                      <div>
                        {products.map((product) => (
                          <div key={product.id}>
                            <Grid
                              container
                              spacing={2}
                              justify="space-between"
                              alignItems="center"
                            >
                              <Grid item>
                                <Typography variant="h4" component="h2">
                                  {prismicText(
                                    product?.data?.products_category
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item>
                                {typeof product.data.products_category_link
                                  .url !== 'undefined' && (
                                  <Link
                                    href={
                                      product.data.products_category_link.url
                                    }
                                    passHref
                                  >
                                    <Button
                                      component="a"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="small"
                                      color="primary"
                                      endIcon={<KeyboardArrowRight />}
                                    >
                                      {prismicText(product.data.see_more)}
                                    </Button>
                                  </Link>
                                )}
                              </Grid>
                            </Grid>
                            <div className={classes.row}>
                              <Grid container spacing={2}>
                                {product.data.products_list.map(
                                  (product, index) => (
                                    <Grid
                                      key={`product-${index}`}
                                      item
                                      sm={12}
                                      lg={6}
                                    >
                                      <div className={classes.productCard}>
                                        {typeof product.product_image.url !==
                                        'undefined' ? (
                                          <img
                                            src={product.product_image.url}
                                            alt=""
                                            className={classes.productCardMedia}
                                          />
                                        ) : (
                                          <Skeleton
                                            variant="rect"
                                            animation={false}
                                            width={140}
                                            height={108}
                                          />
                                        )}
                                        <div
                                          className={classes.productCardContent}
                                        >
                                          <Typography variant="body2">
                                            {truncateString(
                                              prismicText(product.product_name),
                                              50
                                            )}
                                          </Typography>
                                          <div>
                                            {typeof product.product_url.url !==
                                              'undefined' && (
                                              <Link
                                                href={product.product_url.url}
                                                passHref
                                              >
                                                <Button
                                                  component="a"
                                                  size="small"
                                                  variant="contained"
                                                  color="secondary"
                                                  onClick={() =>
                                                    trackEvent({
                                                      category: 'Confirmation',
                                                      action: `Go to product to buy ${product.product_url.url}`,
                                                    })
                                                  }
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {prismicText(
                                                    products[0].data.buy_button
                                                  )}
                                                </Button>
                                              </Link>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </Grid>
                                  )
                                )}
                              </Grid>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </Container>
      </main>
    </Layout>
  )
}

export const getStaticProps = async ({ locale, previewData }) => {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  const confirmation: PrismicConfirmation | null = await getSingleDocument(
    client,
    lang,
    'confirmation'
  )
  const instagram = await getSingleDocument(client, lang, 'instagramwall')
  const general: PrismicGeneralConfig | null = await getSingleDocument(
    client,
    lang,
    'general'
  )
  const takeback = await getSingleDocument(client, lang, 'take_back')
  const basket = await getSingleDocument(client, lang, 'basket')
  const inStoreText = await getSingleDocument(client, lang, 'instore')
  const productsResponse = await getByType(client, 'products_to_buy', lang)
  const productsResults = productsResponse?.results || []
  const accountRes = await getSingleDocument(client, lang, 'account')
  const faqRes = await getSingleDocument(client, lang, 'faq')
  const banner = await getSingleDocument(client, lang, 'banner')

  return {
    props: {
      doc: confirmation,
      takeback,
      main: general,
      inStoreText,
      instagram,
      basket,
      accountRes,
      faqRes,
      products: productsResults,
      banner,
      i18nResources: await getTranslations(locale, ['texts']),
    },
    revalidate: 300,
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Confirmation)
